import * as React from 'react';

import {RouteComponentProps} from 'react-router';
import LessonApi from '../modules/lessons/api/LessonApi';
import Lesson from '../modules/lessons/domain/aggregates/lesson/Lesson';
import {Link} from 'react-router-dom';
import Page from './Page';
import Registry from '../global/Registry';
import LessonResultApi from '../modules/lessons/api/LessonResultApi';
import EntityId from '../../foundation/domain/EntityId';
import * as _ from 'lodash';
import ExerciseApi from '../modules/lessons/api/ExerciseApi';
import { Button, Label } from 'semantic-ui-react';
import Exercise from '../modules/lessons/domain/aggregates/exercise/Exercise';

interface Props {}

interface State {
	lessons: Lesson[];
	exercisesToReview: Exercise[];
}

export default class HomePageOld extends Page<RouteComponentProps<Props>, State> {

	public constructor (props: RouteComponentProps<Props>) {
		super(props);

		this.state = { lessons: [], exercisesToReview: [] };
	}

	async componentWillMount () {
		const [lessons, exercisesToReview] = await Promise.all([
			LessonApi.getInstance().getAll(),
			ExerciseApi.getInstance().getAllToRepeatByUserId(Registry.getInstance().me.getId()),
			LessonResultApi.getInstance().getAllByUserId(Registry.getInstance().me.getId())
		]);

		this.setState({lessons, exercisesToReview});
	}

	protected isLoading (): any {
		return this.state.lessons.length === 0;
	}

	protected renderTitle (): string {
		return 'Brain Brooder - Digital Math & Science Education';
	}

	protected renderDescription (): string {
		return 'Learn Math and Science deeply, by taking small but dense lessons that are followed by intelligently repeated exercises, such that you never forget.';
	}

	protected renderContent () {
		const lessons = this.state.lessons;

		const maxX = _(lessons).maxBy(lesson => lesson.getPosition().getX()).getPosition().getX();
		const maxY = _(lessons).maxBy(lesson => lesson.getPosition().getY()).getPosition().getY();

		const svgWidth = maxX * 500 + 400;
		const svgHeight = maxY * 300 + 200;

		const numberOfExercisesToReview = this.state.exercisesToReview.length;

		const completedColor = '#4183c4';
		// const completedColor = '#6daf6d';
		const nextColor = '#ffffff';
		const incompleteColor = '#808080';

		return (
			<div>
				<div style={{position: 'absolute', top: '80px', width: '400px', textAlign: 'left'}}>
					<h4>What is Brain Brooder?</h4>
					<p>
						Brain Brooder is a digital platform for learning math and science,
						with a special focus on building lasting proficiency,
						and on mapping the dependencies between proficiencies.
					</p>

					<h4>Where do I start?</h4>
					<p>
						You can start with any lesson at the top,
						such as <strong>Introduction to sets</strong> or <strong>Exponentiation</strong> or <strong>Atoms</strong>,
						as these lessons don't have dependencies on earlier lessons.
					</p>

					<h4>Can I volunteer in creating content?</h4>
					<p>
						I am currently looking for native English speaking volunteers who are doing or have finished a Master or Ph. D. in Mathematics, Physics or Chemistry.
						You will be mentioned as a contributor below the lessons in which you contribute.
					</p>

					<h4>How do I contact you?</h4>
					<p>
						You can send an email to erik at brainbrooder.com,
						using an <strong>@</strong> instead of the word <strong>at</strong>.
					</p>
				</div>

				{numberOfExercisesToReview > 0 ? <Link to='/exercises/review' disabled={numberOfExercisesToReview === 0}><Button style={{marginBottom: '24px'}} inverted disabled={numberOfExercisesToReview === 0}>Review exercises <Label style={{marginLeft: '16px'}} color='grey' circular>{numberOfExercisesToReview}</Label></Button></Link> : null}
				<div className='lesson-container'>
					{this.state.lessons.map(lesson => <LessonComponent key={lesson.getId().getValue() as number} lesson={lesson} />)}
					<div style={{position: 'absolute', top: 0, left: 0, zIndex: 51, height: svgHeight + 16 + 'px', pointerEvents: 'none'}}>
						<svg width={svgWidth} height={svgHeight} xmlns='http://www.w3.org/2000/svg'>
							<g>
								<title>Connections</title>
								<defs>
									<linearGradient id='completed-to-completed' x1='0' x2='0' y1='0' y2='1'>
										<stop offset='0%'  stopColor={completedColor} />
										<stop offset='100%' stopColor={completedColor}/>
									</linearGradient>
									<linearGradient id='completed-to-incomplete' x1='0' x2='0' y1='0' y2='1'>
										<stop offset='0%'  stopColor={completedColor} />
										<stop offset='100%' stopColor={incompleteColor}/>
									</linearGradient>
									<linearGradient id='completed-to-next' x1='0' x2='0' y1='0' y2='1'>
										<stop offset='0%'  stopColor={completedColor} />
										<stop offset='100%' stopColor={nextColor}/>
									</linearGradient>
									<linearGradient id='next-to-incomplete' x1='0' x2='0' y1='0' y2='1'>
										<stop offset='0%'  stopColor={nextColor} />
										<stop offset='100%' stopColor={incompleteColor}/>
									</linearGradient>
									<linearGradient id='next-to-completed' x1='0' x2='0' y1='0' y2='1'>
										<stop offset='0%'  stopColor={nextColor} />
										<stop offset='100%' stopColor={completedColor}/>
									</linearGradient>
									<linearGradient id='incomplete-to-incomplete' x1='0' x2='0' y1='0' y2='1'>
										<stop offset='0%'  stopColor={incompleteColor} />
										<stop offset='100%' stopColor={incompleteColor} />
									</linearGradient>
									<linearGradient id='incomplete-to-completed' x1='0' x2='0' y1='0' y2='1'>
										<stop offset='0%'  stopColor={incompleteColor} />
										<stop offset='100%' stopColor={completedColor}/>
									</linearGradient>
								</defs>

								{lessons.map(lesson => <g key={lesson.getId().getValue() as number}>{lesson.getPrerequisiteLessonIds().map(prerequisiteId => <ConnectionComponent key={lesson.getId().getValue() + '-' + prerequisiteId.getValue()} from={Registry.getInstance().lessonRepository.getById(prerequisiteId)} to={lesson} />)}</g>)}
							</g>
						</svg>
					</div>
				</div>
			</div>
		);
	}
}

interface LessonProps {
	lesson: Lesson;
}

function isCompleted (lessonId: EntityId<Lesson>) {
	const lessonResult = Registry.getInstance().lessonResultRepository.getByUserIdAndLessonId(Registry.getInstance().me.getId(), lessonId);
	return lessonResult !== undefined && lessonResult.isCompleted();
}

function getLessonStatus (lesson: Lesson) {

	if (isCompleted(lesson.getId())) {
		return 'completed';
	}

	if (lesson.getPrerequisiteLessonIds().every(lessonId => isCompleted(lessonId))) {
		return 'next';
	}

	return 'incomplete';
}

function LessonComponent (props: LessonProps) {

	const lesson = props.lesson;

	return (
		<Link className={'lesson ' + getLessonStatus(lesson)} to={lesson.getSlug()} style={{ left: lesson.getPosition().getX() * 500, top: lesson.getPosition().getY() * 300}}>
			<div>
				<div className='header'>
					{lesson.getTitle()}
				</div>
				<div className='content'>
					{lesson.getSubtitle()}
				</div>
			</div>
		</Link>
	);
}

interface ConnectionProps {
	from: Lesson;
	to: Lesson;
}

function ConnectionComponent (props: ConnectionProps) {

	const fromLesson = props.from;
	const toLesson = props.to;

	const x1 = fromLesson.getPosition().getX() * 500 + 200;
	const y1 = fromLesson.getPosition().getY() * 300 + 200 - 2;
	const x2 = toLesson.getPosition().getX() * 500 + 200;
	const y2 = toLesson.getPosition().getY() * 300 + 2;

	const fromLessonStatus = getLessonStatus(fromLesson);
	const toLessonStatus = getLessonStatus(toLesson);

	const topColor = fromLessonStatus === 'incomplete' ? '#808080' : fromLessonStatus === 'next' ? '#ffffff' : '#4183c4';
	const bottomColor = toLessonStatus === 'incomplete' ? '#808080' : toLessonStatus === 'next' ? '#ffffff' : '#4183c4';

	return (
		<g>
			<line x1={x1} y1={y1} x2={x2 + 0.001} y2={y2 + 0.001} strokeWidth='4' stroke={'url(#' + fromLessonStatus + '-to-' + toLessonStatus + ')'} />
			<circle cx={x1} cy={y1} r={8} stroke={topColor} fillOpacity='1' strokeWidth='4' fill='#1D1D1D' />
			<circle cx={x2} cy={y2} r={8} stroke={bottomColor} fillOpacity='1' strokeWidth='4' fill='#1D1D1D' />
		</g>
	);
}


// WEBPACK FOOTER //
// ./code/project/pages/HomePageOld.tsx